import { useState, useEffect, useRef } from 'react';

/**
 * Hook to provide visual feedback when price changes
 * Returns a className that flashes green if price increased, red if decreased
 * @param currentPrice - The current price value
 * @param flashDuration - Duration of the flash effect in milliseconds (default: 500ms)
 * @returns Object with flashClassName and direction ('up' | 'down' | null)
 */
export const usePriceFlash = (
    currentPrice: number | null | undefined,
    flashDuration: number = 500
): { flashClassName: string; direction: 'up' | 'down' | null } => {
    const [flashClassName, setFlashClassName] = useState<string>('');
    const [direction, setDirection] = useState<'up' | 'down' | null>(null);
    const previousPriceRef = useRef<number | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Only flash if we have a valid current price and a previous price to compare
        if (
            currentPrice !== null &&
            currentPrice !== undefined &&
            currentPrice > 0 &&
            previousPriceRef.current !== null &&
            previousPriceRef.current > 0 &&
            currentPrice !== previousPriceRef.current
        ) {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Determine direction
            const isUp = currentPrice > previousPriceRef.current;
            setDirection(isUp ? 'up' : 'down');

            // Apply flash class
            setFlashClassName(
                isUp
                    ? 'transition-colors duration-500 bg-green-500/20 text-green-500'
                    : 'transition-colors duration-500 bg-red-500/20 text-red-500'
            );

            // Clear flash after duration
            timeoutRef.current = setTimeout(() => {
                setFlashClassName('');
                setDirection(null);
            }, flashDuration);
        }

        // Update previous price
        if (currentPrice !== null && currentPrice !== undefined && currentPrice > 0) {
            previousPriceRef.current = currentPrice;
        }

        // Cleanup
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [currentPrice, flashDuration]);

    return { flashClassName, direction };
};
